import { useLocation } from 'react-router-dom';

/**
 * Premium contextual route-transition loader.
 *
 * Visual layers:
 *  1. Slim shimmer progress bar at top (system feedback)
 *  2. Soft radial aurora glow behind the text (atmosphere)
 *  3. Animated gradient orb with concentric pulse rings (focal point)
 *  4. Letter-staggered headline reveal + fading sub-line (contextual copy)
 *
 * Copy stays honest: it names what is being opened, not fake background work.
 * The text frame appears after 120ms so cached/instant transitions never flash.
 */

type RouteCopy = { title: string; sub?: string };

const ROUTE_COPY: Array<{ test: (p: string) => boolean; copy: RouteCopy }> = [
  { test: (p) => p.startsWith('/research-advisor'), copy: { title: 'Opening your research focus', sub: 'Aim, questions, and analytical lens' } },
  { test: (p) => p.startsWith('/transcripts'),       copy: { title: 'Loading your sources',         sub: 'Transcripts and documents' } },
  { test: (p) => p.startsWith('/findings') || p.startsWith('/codebook'), copy: { title: 'Opening findings and codebook', sub: 'Themes, codes, and saturation' } },
  { test: (p) => p.startsWith('/draft'),             copy: { title: 'Opening your draft workspace', sub: 'Evidence-linked report' } },
  { test: (p) => p.startsWith('/project/'),          copy: { title: 'Opening your project',         sub: 'Sources and analysis' } },
  { test: (p) => p === '/projects' || p === '/home', copy: { title: 'Loading your projects',        sub: 'Your research at a glance' } },
  { test: (p) => p.startsWith('/settings'),          copy: { title: 'Opening settings',             sub: 'Preferences and account' } },
  { test: (p) => p.startsWith('/inbox'),             copy: { title: 'Opening your inbox',           sub: 'Notifications and updates' } },
  { test: (p) => p.startsWith('/admin'),             copy: { title: 'Opening admin',                sub: 'Operational controls' } },
  { test: (p) => p.startsWith('/onboarding'),        copy: { title: 'Setting things up',            sub: 'A few quick steps' } },
];

function copyForPath(path: string): RouteCopy {
  return ROUTE_COPY.find((r) => r.test(path))?.copy ?? { title: 'Loading' };
}

export function RouteLoader() {
  const location = useLocation();
  const { title, sub } = copyForPath(location.pathname);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background relative overflow-hidden">
      {/* Subtle dot grid for depth */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.5]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.06) 0.5px, transparent 0)',
          backgroundSize: '22px 22px',
        }}
      />

      {/* Aurora glow behind the focal area */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-[28vh] -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full pointer-events-none blur-3xl opacity-60 animate-route-aurora"
        style={{
          background:
            'radial-gradient(circle, hsl(var(--primary) / 0.28) 0%, hsl(var(--primary) / 0.08) 40%, transparent 70%)',
        }}
      />

      {/* Slim shimmer progress bar — system feedback */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-[2px] bg-primary/10 overflow-hidden">
        <div className="route-loader-bar h-full w-1/3 rounded-full" />
      </div>

      {/* Contextual focal area */}
      <div
        className="relative flex flex-col items-center justify-start pt-[24vh] px-6"
        aria-live="polite"
        aria-busy="true"
      >
        {/* Pulse rings + gradient orb */}
        <div className="relative h-14 w-14 mb-7 flex items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border border-primary/30 animate-route-ring [animation-delay:0ms]"
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border border-primary/20 animate-route-ring [animation-delay:600ms]"
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border border-primary/10 animate-route-ring [animation-delay:1200ms]"
          />
          <span
            aria-hidden="true"
            className="relative h-3 w-3 rounded-full bg-gradient-to-br from-primary to-primary/60 shadow-[0_0_20px_hsl(var(--primary)/0.55)] animate-route-orb"
          />
        </div>

        {/* Headline — h1 so Lighthouse picks it as LCP candidate */}
        <h1 className="text-center text-[22px] sm:text-[26px] font-semibold tracking-tight text-foreground">
          <span className="route-loader-shimmer">
            {title.split('').map((ch, i) => (
              <span
                key={`${ch}-${i}`}
                className="inline-block animate-route-letter"
                style={{
                  animationDelay: `${i * 22}ms`,
                  whiteSpace: ch === ' ' ? 'pre' : 'normal',
                }}
              >
                {ch}
              </span>
            ))}
          </span>
        </h1>

        {sub && (
          <p
            className="mt-2.5 text-[13px] text-muted-foreground/90 text-center max-w-[34ch] animate-route-sub"
            style={{ animationDelay: `${Math.min(title.length * 22, 600) + 80}ms` }}
          >
            {sub}
          </p>
        )}

        {/* Three-dot breath */}
        <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/70 animate-route-dot [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/70 animate-route-dot [animation-delay:160ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/70 animate-route-dot [animation-delay:320ms]" />
        </div>
      </div>
    </div>
  );
}
